import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { toast } from 'sonner'
import { router } from '@inertiajs/react'
import type { CreditPack, OrganizationBilling } from '@/types/conversations'

interface LowCreditsCardProps {
  organization: OrganizationBilling
  creditPacks: CreditPack[]
}

export function LowCreditsCard({ organization, creditPacks }: LowCreditsCardProps) {
  const { t } = useTranslation('conversations')
  const [isDismissed, setIsDismissed] = useState(false)
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false)
  const [pendingPurchase, setPendingPurchase] = useState<CreditPack | null>(null)
  const [isPurchasing, setIsPurchasing] = useState(false)
  const [purchaseError, setPurchaseError] = useState<string | null>(null)

  // Convert to numbers (backend may send strings)
  const creditsRemaining = Number(organization.credits_remaining) || 0
  const creditsTotal = Number(organization.credits_total) || 0

  // Calculate credits percentage
  const creditsPercentage = creditsTotal > 0
    ? (creditsRemaining / creditsTotal) * 100
    : 0

  // Determine visibility conditions
  const isPaidUser = organization.current_plan !== 'free'
  const isLowCredits = creditsPercentage < 5 && creditsRemaining > 0
  const canPurchase = organization.can_purchase_credits && creditPacks.length > 0

  // Only show for paid users with low credits who can purchase (and not dismissed)
  if (!isPaidUser || !isLowCredits || !canPurchase || isDismissed) {
    return null
  }

  const handlePurchaseCredits = (creditPack: CreditPack) => {
    setPendingPurchase(creditPack)
    setPurchaseError(null)
    setShowPurchaseDialog(true)
  }

  const confirmPurchaseCredits = async () => {
    if (!pendingPurchase) return

    setIsPurchasing(true)
    setPurchaseError(null)

    try {
      const response = await fetch('/billing/credit_packs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({
          credits_amount: pendingPurchase.credits,
          price_usd: pendingPurchase.price
        })
      })

      const data = await response.json()

      if (data.success && data.status === 'succeeded') {
        setShowPurchaseDialog(false)
        setPendingPurchase(null)
        toast.success(t('credits.purchaseSuccess'))
        router.reload()
      } else if (data.success && data.status === 'requires_action') {
        setPurchaseError(t('credits.requires3dSecure'))
        setIsPurchasing(false)
      } else {
        const errorMessage = data.errors?.join(', ') || data.error || t('credits.purchaseFailed')
        setPurchaseError(errorMessage)
        setIsPurchasing(false)
      }
    } catch (error) {
      console.error('Error purchasing credits:', error)
      setPurchaseError(t('credits.purchaseFailed'))
      setIsPurchasing(false)
    }
  }

  return (
    <>
      <div className="mx-4 mb-2 rounded-xl border border-orange-200 bg-orange-50 dark:border-orange-900 dark:bg-orange-950/30 p-4 flex-shrink-0 relative">
        <button
          onClick={() => setIsDismissed(true)}
          className="absolute top-2 right-2 p-1 rounded-md hover:bg-orange-200 dark:hover:bg-orange-800 text-orange-600 dark:text-orange-400"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-start gap-3 pr-6">
          <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-orange-800 dark:text-orange-200">
              {t('credits.lowWarning')}
            </div>
            <div className="text-xs text-orange-600 dark:text-orange-400 mt-1">
              {t('credits.remaining', {
                remaining: creditsRemaining.toFixed(1),
                percentage: creditsPercentage.toFixed(0)
              })}
            </div>

            {/* Credit Pack Purchase Buttons (admin only) */}
            {organization.is_admin ? (
              <div className="mt-3 grid grid-cols-2 gap-2">
                {creditPacks.slice(0, 4).map((pack) => (
                  <Button
                    key={pack.credits}
                    variant="outline"
                    size="sm"
                    className="h-auto py-2 px-3 border-orange-300 dark:border-orange-700 hover:bg-orange-100 dark:hover:bg-orange-900/50 text-xs"
                    onClick={() => handlePurchaseCredits(pack)}
                    disabled={isPurchasing}
                  >
                    {pack.credits} credits (${pack.price})
                  </Button>
                ))}
              </div>
            ) : (
              <p className="mt-2 text-xs text-orange-600 dark:text-orange-400">
                {t('credits.contactAdmin')}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Credit Pack Purchase Confirmation Dialog */}
      <AlertDialog open={showPurchaseDialog} onOpenChange={(open) => {
        if (!isPurchasing) {
          setShowPurchaseDialog(open)
          if (!open) {
            setPendingPurchase(null)
            setPurchaseError(null)
          }
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('credits.confirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {pendingPurchase && t('credits.confirmMessage', {
                  credits: pendingPurchase.credits,
                  price: pendingPurchase.price.toFixed(2)
                })}
                {purchaseError && (
                  <div className="mt-3 p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
                    {purchaseError}
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPurchasing}>
              {t('common:buttons.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                confirmPurchaseCredits()
              }}
              disabled={isPurchasing}
            >
              {isPurchasing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('credits.purchasing')}
                </>
              ) : (
                t('credits.confirmPurchase', { price: pendingPurchase?.price.toFixed(2) || '0' })
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
